<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import { RrButton, RrSelect, RrInput, RrCheckbox, AdminStatusTag, RrTag } from '@xjlv2/admin-layout'
import type {
  DataScopeGrant,
  DataResource,
  ModuleMetadata,
  OpenApp,
  TenantOpenAppGrant,
  UpdateTenantOpenAppGrantRequest
} from '../../types'

const props = defineProps<{
  apps: OpenApp[]
  grants: TenantOpenAppGrant[]
  modules: ModuleMetadata[]
  dataResources: DataResource[]
  canGrant: boolean
  loading: boolean
}>()

const emit = defineEmits<{
  update: [appId: number, payload: UpdateTenantOpenAppGrantRequest]
}>()

const selectedAppId = ref<number | null>(null)
const form = ref({
  moduleCodes: [] as string[],
  permissions: [] as string[],
  dataScopes: {} as Record<string, DataScopeGrant>,
  status: 'ACTIVE',
  expiresAt: ''
})

const selectedApp = computed(() => props.apps.find((app) => app.id === selectedAppId.value) ?? props.apps[0] ?? null)
const selectedGrant = computed(() =>
  selectedApp.value ? props.grants.find((grant) => grant.appId === selectedApp.value?.id) ?? null : null
)
const selectedModules = computed(() =>
  props.modules.filter((module) => form.value.moduleCodes.includes(module.moduleCode))
)
const availableApiResources = computed(() =>
  selectedModules.value.flatMap((module) =>
    module.apis
      .filter((api) => !api.permission.startsWith('system:'))
      .map((api) => ({
        ...api,
        moduleCode: module.moduleCode,
        moduleName: module.moduleName,
        key: `${module.moduleCode}:${api.method}:${api.path}`
      }))
  )
)
const availablePermissions = computed(() =>
  Array.from(new Set(availableApiResources.value.map((api) => api.permission))).sort()
)
const apiGroups = computed(() =>
  selectedModules.value.map((module) => ({
    moduleCode: module.moduleCode,
    moduleName: module.moduleName,
    items: availableApiResources.value.filter((api) => api.moduleCode === module.moduleCode),
    dataResources: module.dataResources
  }))
)
const availableDataResources = computed(() => {
  const resources = new Map<string, DataResource>()
  for (const module of selectedModules.value) {
    for (const resource of module.dataResources) {
      resources.set(resource.code, resource)
    }
  }
  return Array.from(resources.values())
})
const selectedApiCount = computed(() =>
  availableApiResources.value.filter((api) => form.value.permissions.includes(api.permission)).length
)
const selectedModuleCount = computed(() => form.value.moduleCodes.length)

function grantFor(app: OpenApp) {
  return props.grants.find((grant) => grant.appId === app.id) ?? null
}

watch(
  () => props.apps,
  (apps) => {
    if (selectedAppId.value === null && apps[0]) {
      selectApp(apps[0])
    }
  },
  { immediate: true }
)

function selectApp(app: OpenApp) {
  selectedAppId.value = app.id
  const grant = grantFor(app)
  form.value = {
    moduleCodes: grant?.moduleCodes ? [...grant.moduleCodes] : props.modules.slice(0, 1).map((module) => module.moduleCode),
    permissions: grant?.permissions ? [...grant.permissions] : [],
    dataScopes: Object.fromEntries((grant?.dataScopes ?? []).map((scope) => [scope.resourceCode, scope])),
    status: grant?.status ?? 'ACTIVE',
    expiresAt: grant?.expiresAt ? grant.expiresAt.slice(0, 16) : ''
  }
}

function toggleModule(moduleCode: string) {
  const selected = new Set(form.value.moduleCodes)
  selected.has(moduleCode) ? selected.delete(moduleCode) : selected.add(moduleCode)
  form.value.moduleCodes = Array.from(selected).sort()
  cleanupGrantForm()
}

function togglePermission(permission: string) {
  const selected = new Set(form.value.permissions)
  selected.has(permission) ? selected.delete(permission) : selected.add(permission)
  form.value.permissions = Array.from(selected).sort()
}

function setModuleApiSelection(moduleCode: string, checked: boolean) {
  const selected = new Set(form.value.permissions)
  for (const api of availableApiResources.value.filter((item) => item.moduleCode === moduleCode)) {
    checked ? selected.add(api.permission) : selected.delete(api.permission)
  }
  form.value.permissions = Array.from(selected).sort()
}

function allModuleApisSelected(moduleCode: string) {
  const apis = availableApiResources.value.filter((api) => api.moduleCode === moduleCode)
  return apis.length > 0 && apis.every((api) => form.value.permissions.includes(api.permission))
}

function moduleSelectedApiCount(moduleCode: string) {
  return availableApiResources.value
    .filter((api) => api.moduleCode === moduleCode)
    .filter((api) => form.value.permissions.includes(api.permission)).length
}

function cleanupGrantForm() {
  const permissionSet = new Set(availablePermissions.value)
  form.value.permissions = form.value.permissions.filter((permission) => permissionSet.has(permission))
  const resourceSet = new Set(availableDataResources.value.map((resource) => resource.code))
  form.value.dataScopes = Object.fromEntries(
    Object.entries(form.value.dataScopes).filter(([resourceCode]) => resourceSet.has(resourceCode))
  )
}

function setScope(resource: DataResource, scopeType: DataScopeGrant['scopeType']) {
  form.value.dataScopes = {
    ...form.value.dataScopes,
    [resource.code]: {
      resourceCode: resource.code,
      scopeType,
      customDeptIds: []
    }
  }
}

function handleScopeChange(resource: DataResource, val: string) {
  setScope(resource, val as DataScopeGrant['scopeType'])
}

function submit() {
  if (!selectedApp.value || !props.canGrant) return
  const dataScopes = availableDataResources.value.map((resource) => {
    return form.value.dataScopes[resource.code] ?? {
      resourceCode: resource.code,
      scopeType: 'ALL' as const,
      customDeptIds: []
    }
  })
  emit('update', selectedApp.value.id, {
    moduleCodes: form.value.moduleCodes,
    permissions: form.value.permissions,
    dataScopes,
    status: form.value.status,
    expiresAt: form.value.expiresAt ? new Date(form.value.expiresAt).toISOString() : null
  })
}
</script>

<template>
  <section class="grid">
    <article class="panel">
      <div class="panel-head">
        <div>
          <p class="eyebrow">Open Platform</p>
          <h2>开放应用</h2>
        </div>
        <span>{{ apps.length }} 个应用</span>
      </div>
      <div class="permission-list">
        <div
          v-for="app in apps"
          :key="app.id"
          class="row selectable"
          :class="{ selected: selectedApp?.id === app.id }"
          @click="selectApp(app)"
          style="cursor: pointer;"
        >
          <strong>{{ app.appName }}</strong>
          <small>{{ app.clientId }} · {{ app.appType }} · {{ app.developerName ?? app.developerId }}</small>
          <AdminStatusTag :type="grantFor(app)?.status === 'ACTIVE' ? 'success' : 'default'">
            {{ grantFor(app)?.status ?? '未授权' }}
          </AdminStatusTag>
        </div>
      </div>
    </article>

    <article class="panel">
      <div class="panel-head">
        <div>
          <p class="eyebrow">Tenant Grant</p>
          <h2>租户授权</h2>
        </div>
        <span>{{ selectedApp?.appName ?? '未选择' }}</span>
      </div>

      <form v-if="selectedApp" class="form-grid" @submit.prevent="submit">
        <label>
          状态
          <RrSelect v-model:value="form.status" :disabled="!canGrant || loading" :options="[
            { label: '已授权', value: 'ACTIVE' },
            { label: '暂停', value: 'SUSPENDED' },
            { label: '撤销', value: 'REVOKED' }
          ]" />
        </label>
        <label>
          到期时间
          <RrInput v-model:value="form.expiresAt"  :disabled="!canGrant || loading" />
        </label>

        <div>
          <h3>授权模块</h3>
          <div class="check-grid compact-check-grid">
            <label v-for="module in modules" :key="module.moduleCode" class="check-row">
              <RrCheckbox
                :checked="form.moduleCodes.includes(module.moduleCode)"
                :disabled="!canGrant || loading"
                @update:checked="toggleModule(module.moduleCode)"
              />
              <span>
                <strong>{{ module.moduleName }}</strong>
                <small>{{ module.moduleCode }} · {{ module.apis.length }} API · {{ module.dataResources.length }} 数据资源</small>
              </span>
            </label>
          </div>
        </div>

        <div>
          <div class="section-head">
            <h3>API 权限</h3>
            <span>{{ selectedApiCount }} / {{ availableApiResources.length }} 已授权</span>
          </div>
          <div class="open-api-grant-list">
            <section v-for="group in apiGroups" :key="group.moduleCode" class="open-api-grant-group">
              <div class="api-grant-group-head">
                <span>
                  <strong>{{ group.moduleName }}</strong>
                  <small>{{ group.moduleCode }} · {{ moduleSelectedApiCount(group.moduleCode) }} / {{ group.items.length }} API</small>
                </span>
                <div class="api-grant-tools">
                  <RrButton
                    type="default"
                    :disabled="!canGrant || loading || group.items.length === 0 || allModuleApisSelected(group.moduleCode)"
                    @click="setModuleApiSelection(group.moduleCode, true)"
                  >
                    全选 API
                  </RrButton>
                  <RrButton
                    type="default"
                    :disabled="!canGrant || loading || moduleSelectedApiCount(group.moduleCode) === 0"
                    @click="setModuleApiSelection(group.moduleCode, false)"
                  >
                    清空
                  </RrButton>
                </div>
              </div>
              <label v-for="api in group.items" :key="api.key" class="open-api-grant-row">
                <RrCheckbox
                  :checked="form.permissions.includes(api.permission)"
                  :disabled="!canGrant || loading"
                  @update:checked="togglePermission(api.permission)"
                />
                <RrTag :type="api.method.toLowerCase() === 'get' ? 'success' : api.method.toLowerCase() === 'post' ? 'warning' : 'primary'">{{ api.method }}</RrTag>
                <span>
                  <strong>{{ api.path }}</strong>
                  <small>{{ api.permission }}</small>
                </span>
              </label>
              <p v-if="group.items.length === 0" class="empty-state">该模块没有可开放 API。</p>
              <div v-if="group.dataResources.length" class="resource-hint-strip">
                <span v-for="resource in group.dataResources" :key="resource.code">{{ resource.code }}</span>
              </div>
            </section>
            <p v-if="availableApiResources.length === 0" class="empty-state">请选择模块后配置 API 权限。</p>
          </div>
        </div>

        <div>
          <div class="section-head">
            <h3>数据权限</h3>
            <span>{{ availableDataResources.length }} 个数据资源</span>
          </div>
          <div class="data-scope-editor">
            <div v-for="resource in availableDataResources" :key="resource.code" class="data-scope-card">
              <strong>{{ resource.code }}</strong>
              <small>{{ resource.tableName }} · {{ resource.deptColumn }}</small>
              <RrSelect
                :value="form.dataScopes[resource.code]?.scopeType ?? 'ALL'"
                :disabled="!canGrant || loading"
                :options="[
                  { label: '全部数据', value: 'ALL' },
                  { label: '本部门', value: 'DEPT' },
                  { label: '本部门及下级', value: 'DEPT_AND_CHILD' },
                  { label: '仅本人', value: 'SELF' }
                ]"
                @update:value="handleScopeChange(resource, $event)"
              />
            </div>
          </div>
        </div>

        <div class="grant-summary">
          <span>{{ selectedModuleCount }} 个模块</span>
          <span>{{ selectedApiCount }} 个 API 权限</span>
          <span>{{ availableDataResources.length }} 个数据资源</span>
        </div>
        <RrButton type="primary" html-type="submit" :disabled="!canGrant || loading">保存开放授权</RrButton>
      </form>
      <p v-else class="empty-state">请选择一个开放应用。</p>
    </article>
  </section>
</template>
